/**
 * PeopleDetail Component
 * 
 * Displays comprehensive person information in the People module.
 * Shows person details, tasks, timesheets, activity, and files.
 * 
 * @param {string} peopleId - The ID of the person
 */

import { Container, Row, Col, Nav } from 'react-bootstrap';
import { Link, useLocation, useParams } from 'react-router-dom';
import PeopleOverview from './PeopleOverview';
import PeopleTasks from './PeopleTasks';
import PeopleTimesheets from './PeopleTimesheets';
import PeopleActivity from './PeopleActivity';
import PeopleFiles from './PeopleFiles';
import './PeopleDetail.css';

const PeopleDetail = () => {
    const location = useLocation();
    const { peopleId } = useParams();
    const currentPath = location.pathname;

    // Sample person data
    const personData = {
        id: peopleId || 1,
        name: 'John Doe',
        role: 'Frontend Developer',
        department: 'Marketing',
        avatar: 'https://randomuser.me/api/portraits/men/32.jpg',
        email: 'john.doe@example.com',
        phone: '+1 (555) 123-4567',
        joiningDate: '14 March 2016',
        experience: '5 years',
        teamLead: 'Jane Smith',
        manager: 'Sarah Wilson',
        status: 'Active'
    };

    // Get dynamic heading based on current section
    const getSectionHeading = () => {
        if (currentPath.includes('/tasks')) return 'People Tasks';
        if (currentPath.includes('/timesheets')) return 'People Timesheets';
        if (currentPath.includes('/activity')) return 'People Activity';
        if (currentPath.includes('/files')) return 'People Files';
        return 'People Overview';
    };

    // Check if we're in specific sections
    const isOverviewSection = currentPath === `/people/detail/${peopleId}`;
    const isTasksSection = currentPath.includes('/tasks');
    const isTimesheetsSection = currentPath.includes('/timesheets');
    const isActivitySection = currentPath.includes('/activity');
    const isFilesSection = currentPath.includes('/files');

    return (
        <Container fluid className="people-page">
            <Row>
                <Col lg={4}>
                    <h1 className="display-1">{getSectionHeading()}</h1>
                    <div className="display-2">{personData.name} • {personData.role}</div>
                </Col>
                <Col lg={8}>
                    <div className="text-end">
                        <Nav className="c-nav-tabs">
                            <Nav.Item>
                                <Nav.Link
                                    as={Link}
                                    to={`/people/detail/${peopleId}`}
                                    active={isOverviewSection}
                                >
                                    Overview
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link
                                    as={Link}
                                    to={`/people/detail/${peopleId}/tasks`}
                                    active={isTasksSection}
                                >
                                    Tasks
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link
                                    as={Link}
                                    to={`/people/detail/${peopleId}/timesheets`}
                                    active={isTimesheetsSection}
                                >
                                   Timesheets
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link
                                    as={Link}
                                    to={`/people/detail/${peopleId}/activity`}
                                    active={isActivitySection}
                                >
                                    Activity
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link
                                    as={Link}
                                    to={`/people/detail/${peopleId}/files`}
                                    active={isFilesSection}
                                >
                                    Files
                                </Nav.Link>
                            </Nav.Item>
                        </Nav>
                    </div>
                </Col>
            </Row>
            <Row className="mt-0 gy-4">
                <Col lg={12}>
                    {isOverviewSection && (
                        <PeopleOverview 
                            peopleId={peopleId}
                            personData={personData}
                        />
                    )}

                    {isTasksSection && (
                        <PeopleTasks />
                    )}

                    {isTimesheetsSection && (
                        <PeopleTimesheets />
                    )}

                    {isActivitySection && (
                        <PeopleActivity />
                    )}

                    {isFilesSection && (
                        <PeopleFiles />
                    )}
                </Col>
            </Row>
        </Container>
    );
};

export default PeopleDetail;
